import { DndContext, DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useTheme } from '@libs/useTheme'
import { useEffect, useState } from 'react'
import { GoGrabber } from 'react-icons/go'
import { v4 } from 'uuid'
import Box from './Box'

const Table = ({
  tableHeading,
  rows,
  sortable = false,
  onSort,
  items,
}: {
  tableHeading: (JSX.Element | string | number)[]
  rows?: (JSX.Element | string | number)[][]
  sortable?: boolean
  onSort?: (sortedData: any[]) => void
  items?: any[]
}) => {
  const { theme } = useTheme()
  const random_id = v4()

  const getParsedRows = () => {
    return rows?.map((r, index) => ({ data: r, id: index }))
  }

  const [parsedRows, setParsedRows] = useState(getParsedRows())

  useEffect(() => {
    setParsedRows(getParsedRows())
  }, [rows])

  const dragEndHandler = (e: DragEndEvent) => {
    const { active, over } = e
    if (over && active.id !== over.id && parsedRows && items) {
      const oldIndex = parsedRows?.findIndex((f) => {
        return f.id === active.id
      })
      const newIndex = parsedRows?.findIndex((f) => {
        return f.id === over.id
      })
      const rowsPayload = arrayMove(parsedRows, oldIndex, newIndex)
      setParsedRows(rowsPayload)

      const payload = arrayMove(items, oldIndex, newIndex)

      if (onSort) onSort(payload)
    }
  }

  return (
    <table className="w-full table-auto text-left rounded-lg border border-midnight-gray">
      <thead className="">
        <tr
          style={{ backgroundColor: theme.foregroundColour }}
          className="border-b-midnight-gray border-b"
          key={`table-head-${random_id}-container`}
        >
          {tableHeading.map((t) => (
            <th
              className="p-3"
              key={`table-head-${random_id}-${t.toString().length > 0 ? t.toString() : v4()}`}
            >
              {t}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortable && !!parsedRows ? (
          <DndContext
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={dragEndHandler}
          >
            <SortableContext
              items={parsedRows}
              strategy={verticalListSortingStrategy}
            >
              {parsedRows?.map((row, index) => {
                return (
                  <TableItem
                    sortable
                    id={row.id}
                    key={`${row.id}`}
                    className={`py-2 border-b-midnight-gray border-b bg-white`}
                  >
                    {row.data.map((t, i) => (
                      <td className="p-3" key={`table-data-${random_id}-${i}`}>
                        {index === 3 && t === 'Break' ? (
                          <span className="rounded border border-orange-200 text-orange-200 py-0.5 px-1 text-sm">
                            Break
                          </span>
                        ) : (
                          t
                        )}
                      </td>
                    ))}
                  </TableItem>
                )
              })}
            </SortableContext>
          </DndContext>
        ) : (
          parsedRows?.map((row, index) => {
            return (
              <TableItem
                id={row.id}
                key={`${row.id}`}
                className={`py-2 border-b-midnight-gray border-b`}
              >
                {row.data.map((t, i) => (
                  <td className="p-3" key={`table-data-${random_id}-${i}`}>
                    {index === 3 && t === 'Break' ? (
                      <span className="rounded border border-orange-200 text-orange-200 py-0.5 px-1 text-sm">
                        Break
                      </span>
                    ) : (
                      t
                    )}
                  </td>
                ))}
              </TableItem>
            )
          })
        )}
      </tbody>
    </table>
  )
}

const TableItem = ({
  children,
  className,
  id,
  key,
  sortable = false,
}: {
  id: string | number
  children: React.ReactNode
  className?: string
  key?: string
  sortable?: boolean
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`p-3 ${className}`}
      key={`table-row-${id} relative`}
    >
      {!!sortable ? (
        <>
          {children}
          <td className="py-3">
            <Box className={``} {...attributes} {...listeners}>
              <GoGrabber />
            </Box>
          </td>
        </>
      ) : (
        children
      )}
    </tr>
  )
}

export default Table
